import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { servicesRepository } from '../repositories/services.repository'
import { usersRepository } from '../repositories/users.repository'
import { useMutationTracker } from '../hooks/useMutationTracker'
import type { InitPurchaseRequest } from '../models/service'

const SERVICES_KEY = 'services'
const USER_SUBSCRIPTIONS_KEY = 'user-subscriptions'
const INIT_PURCHASE_KEY = 'init-purchase'

export const useServices = () => {
  const queryClient = useQueryClient()

  // Get all available services
  const {
    data: services,
    isLoading: servicesLoading,
    error: servicesError,
    refetch: refetchServices
  } = useQuery({
    queryKey: [SERVICES_KEY],
    queryFn: servicesRepository.getAll
  })

  // Get user subscriptions/purchases
  const {
    data: userSubscriptions,
    isLoading: subscriptionsLoading,
    error: subscriptionsError,
    refetch: refetchSubscriptions
  } = useQuery({
    queryKey: [USER_SUBSCRIPTIONS_KEY],
    queryFn: usersRepository.getMySubscriptions,
    retry: 1
  })

  // Initialize purchase mutation
  const initPurchaseMutation = useMutation({
    mutationKey: [INIT_PURCHASE_KEY],
    mutationFn: (data: InitPurchaseRequest) => servicesRepository.initPurchase(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [USER_SUBSCRIPTIONS_KEY] })
      queryClient.invalidateQueries({ queryKey: ['transactions'] })
    }
  })

  // Track mutation state
  const initPurchaseState = useMutationTracker(INIT_PURCHASE_KEY)

  const dataIsLoading = servicesLoading || subscriptionsLoading
  const dataError = servicesError || subscriptionsError

  const refetch = () => {
    refetchServices()
    refetchSubscriptions()
  }

  return {
    // Data
    services: services || [],
    userSubscriptions: userSubscriptions || [],
    
    // Loading and error states
    dataIsLoading,
    dataError,
    
    // Actions
    initPurchase: initPurchaseMutation.mutate,
    refetch,
    
    // Mutation states
    initPurchaseLoading: initPurchaseState.isPending,
    initPurchaseError: initPurchaseState.error,
    initPurchaseSuccess: initPurchaseState.isSuccess,
    initPurchaseData: initPurchaseState.data
  }
}